import gsap from 'gsap'
import ScrollTrigger from 'gsap/ScrollTrigger'

gsap.registerPlugin(ScrollTrigger)

class EntranceAnimation {
  constructor(lenis, lottie) {
    this.lenis = lenis
    this.lottie = lottie
    this.events()
  }

  events() {
    if (typeof window !== 'undefined') {
      window.__entranceFramesLoaded = false
      window.__entranceComplete = false
      window.__entranceFramesLoadedCount = 0
    }
    if (this.lenis) this.lenis.stop()
    else document.body.classList.add('lock-scroll')

    const entranceCanvas = document.getElementById('js-entrance-canvas')
    const entranceContext = entranceCanvas ? entranceCanvas.getContext('2d') : null
    const heroCanvas = document.getElementById('js-hero')
    const mainContent = document.getElementById('js-main-content')
    const section5 = document.getElementById('js-section-5')
    const contentEnd = document.getElementById('js-content-end')
    const sectionDots = document.getElementById('js-section-dots')
    const bgGradient = document.getElementById('js-bg-gradient')
    const entranceCanvasWrapper = document.getElementById('js-entrance-canvas-wrapper')
    const lottieAnimationWrapper = document.getElementById('js-lottie-animation')
    const lottieLogo = document.getElementById('js-lottie-logo')
    const lcpProxy = document.getElementById('js-lcp-proxy')
    const scrollPrompt = document.getElementById('js-scroll-prompt')

    if (entranceCanvas) {
      gsap.set(entranceCanvas, { autoAlpha: 0 })
    }

    const MOBILE_BREAKPOINT = 769
    const MOBILE_CANVAS_SIZE = 900
    const DESKTOP_CANVAS_SIZE = 1920
    const lottieImgVersion = '1.1'
    const desktopImgVersion = '1.6'
    const mobileImgVersion = '1.6'
    const entranceFrameCount = 180
    const isMobile = window.innerWidth < MOBILE_BREAKPOINT
    const entranceCurrentFrame = (index) => {
      const folder = isMobile ? 'sequence_1_mobile' : 'sequence_1'
      const version = isMobile ? mobileImgVersion : desktopImgVersion
      return `../../assets/${folder}/entrance_0${(index + 1).toString()}.webp?v=${version}`
    }

    const entranceImages = []
    const entranceAnimation = { frame: 0 }
    const IMMEDIATE_ENTRANCE_FRAMES = 40
    const BATCH_ENTRANCE_FRAMES = 20
    let entranceFramesLoadedCount = 0
    let entranceFramesLoadSignaled = false

    const signalEntranceFramesLoaded = () => {
      if (entranceFramesLoadSignaled || typeof window === 'undefined') return
      entranceFramesLoadSignaled = true
      window.__entranceFramesLoaded = true
      window.dispatchEvent(new CustomEvent('entrance:frames-loaded'))
    }

    const markEntranceFrameResolved = () => {
      entranceFramesLoadedCount += 1
      if (typeof window !== 'undefined') {
        window.__entranceFramesLoadedCount = entranceFramesLoadedCount
      }
      if (entranceFramesLoadedCount >= entranceFrameCount) {
        signalEntranceFramesLoaded()
      }
    }

    const setEntranceCanvasDimensions = () => {
      if (!entranceCanvas) return
      const canvasSize = window.innerWidth < MOBILE_BREAKPOINT ? MOBILE_CANVAS_SIZE : DESKTOP_CANVAS_SIZE
      entranceCanvas.width = canvasSize
      entranceCanvas.height = canvasSize
    }

    if (entranceCanvas && entranceContext) {
      setEntranceCanvasDimensions()
      const queueIdleTask = (callback) => {
        if ('requestIdleCallback' in window) {
          window.requestIdleCallback(callback, { timeout: 500 })
          return
        }
        window.setTimeout(callback, 16)
      }

      const loadEntranceFrame = (index) => {
        const img = new Image()
        img.addEventListener('load', markEntranceFrameResolved, { once: true })
        img.addEventListener('error', markEntranceFrameResolved, { once: true })
        img.src = entranceCurrentFrame(index)
        entranceImages[index] = img
      }

      const immediateCount = Math.min(IMMEDIATE_ENTRANCE_FRAMES, entranceFrameCount)
      for (let i = 0; i < immediateCount; i++) {
        loadEntranceFrame(i)
      }

      let nextFrameIndex = immediateCount
      const loadRemainingFrames = () => {
        const end = Math.min(entranceFrameCount, nextFrameIndex + BATCH_ENTRANCE_FRAMES)
        for (let i = nextFrameIndex; i < end; i++) {
          loadEntranceFrame(i)
        }
        nextFrameIndex = end
        if (nextFrameIndex < entranceFrameCount) {
          queueIdleTask(loadRemainingFrames)
        }
      }

      if (nextFrameIndex < entranceFrameCount) {
        queueIdleTask(loadRemainingFrames)
      }
    } else {
      signalEntranceFramesLoaded()
    }

    const renderEntrance = () => {
      if (!entranceCanvas || !entranceContext || !entranceImages[entranceAnimation.frame]) return
      entranceContext.clearRect(0, 0, entranceCanvas.width, entranceCanvas.height)
      entranceContext.drawImage(
        entranceImages[entranceAnimation.frame],
        0,
        0,
        entranceCanvas.width,
        entranceCanvas.height
      )
    }

    const entranceTimeline = gsap.timeline({
      paused: true,
      onStart: () => {
        if (entranceImages[0]) {
          if (entranceImages[0].complete) {
            renderEntrance()
          } else {
            entranceImages[0].onload = () => renderEntrance()
          }
        }
      },
      onComplete: () => {
        if (heroCanvas) heroCanvas.classList.add('is-visible')
        if (typeof window !== 'undefined') {
          window.__entranceComplete = true
          window.dispatchEvent(new CustomEvent('entrance:complete'))
        }
        if (this.lenis) {
          this.lenis.start()
          this.lenis.on('scroll', ScrollTrigger.update)
          const fadeOutScrollPrompt = () => {
            if (scrollPrompt && this.lenis.scroll > 0) {
              this.lenis.off('scroll', fadeOutScrollPrompt)
              gsap.to(scrollPrompt, { opacity: 0, duration: 0.3, ease: 'power2.out' })
            }
          }
          this.lenis.on('scroll', fadeOutScrollPrompt)
        } else {
          document.body.classList.remove('lock-scroll')
          if (scrollPrompt) {
            const fadeOutScrollPrompt = () => {
              if (window.scrollY > 0) {
                window.removeEventListener('scroll', fadeOutScrollPrompt, { passive: true })
                gsap.to(scrollPrompt, { opacity: 0, duration: 0.3, ease: 'power2.out' })
              }
            }
            window.addEventListener('scroll', fadeOutScrollPrompt, { passive: true })
          }
        }
        if (entranceCanvas) gsap.set(entranceCanvas, { autoAlpha: 0 })
        if (mainContent) gsap.set(mainContent, { opacity: 1, visibility: 'visible' })
        if (section5) gsap.set(section5, { opacity: 1, visibility: 'visible' })
        if (contentEnd) gsap.set(contentEnd, { opacity: 1, visibility: 'visible' })
        if (lcpProxy) {
          gsap.to(lcpProxy, {
            autoAlpha: 0,
            duration: 0.2,
            onComplete: () => lcpProxy.remove()
          })
        }
      }
    })

    const mainHeader = document.querySelector('.js-main-header')

    if (sectionDots) {
      gsap.set(sectionDots, { opacity: 0, y: 70 })
    }

    if (entranceCanvas) {
      entranceTimeline.to(entranceCanvas, {
        autoAlpha: 1,
        duration: 1,
        ease: 'power2.inOut'
      }, 0.1)
    }

    entranceTimeline.to(entranceAnimation, {
      frame: entranceFrameCount - 1,
      duration: 5,
      ease: 'power1.out',
      snap: 'frame',
      onUpdate: renderEntrance
    }, 0)

    if (bgGradient) {
      entranceTimeline.fromTo(bgGradient, {
        yPercent: 0
      }, {
        yPercent: -33,
        duration: 5,
        ease: 'power1.out'
      }, 0)
    }

    if (entranceCanvasWrapper) {
      entranceTimeline.fromTo(entranceCanvasWrapper, {
        y: 50,
        scale: 0.90
      }, {
        y: 0,
        scale: 1,
        duration: 2,
        ease: 'power2.out'
      }, 0.2)
    }

    if (lottieLogo) {
      entranceTimeline.to(lottieLogo, {
        opacity: 0,
        duration: 0.6,
        ease: 'power2.out'
      }, 0)
    }

    if (lottieAnimationWrapper) {
      entranceTimeline.to(lottieAnimationWrapper, {
        autoAlpha: 0,
        duration: 2,
        ease: 'power2.inOut',
        scale: 0.9
      }, 0)
    }

    if (mainHeader) {
      entranceTimeline.to(mainHeader, {
        y: 0,
        duration: 2,
        ease: 'power4.inOut'
      }, '-=2')
    }

    if (sectionDots) {
      entranceTimeline.to(sectionDots, {
        opacity: 1,
        y: 0,
        duration: 2,
        ease: 'power4.inOut'
      }, '<')
    }

    if (scrollPrompt) {
      entranceTimeline.to(scrollPrompt, {
        opacity: 1,
        duration: 1,
        ease: 'power4.inOut'
      }, '<')
    }

    const lottieContainer = document.getElementById('js-lottie-canvas')
    const loadingBar = document.getElementById('js-loading-bar')
    const loadingBarFill = loadingBar?.querySelector('.loading-bar__fill')
    const loadingBarText = document.getElementById('js-loading-bar-text')
    const SCROLL_FRAME_COUNT = 762
    const TOTAL_FRAME_COUNT = entranceFrameCount + SCROLL_FRAME_COUNT

    let hasCompleted = false
    let lottieComplete = false

    const tryComplete = () => {
      if (hasCompleted) return
      const framesReady = window.__scrollFramesLoaded === true
      const canComplete = lottieComplete && (framesReady || !loadingBar || !loadingBarFill)
      if (!canComplete) return
      hasCompleted = true
      if (loadingBar) {
        loadingBar.classList.remove('is-visible')
        loadingBar.setAttribute('aria-hidden', 'true')
      }
      entranceTimeline.play(0)
    }

    const showLoadingBarAndStartProgress = () => {
      if (!loadingBar || !loadingBarFill) {
        tryComplete()
        return
      }
      loadingBar.classList.add('is-visible')
      loadingBar.setAttribute('aria-hidden', 'false')

      const updateProgress = () => {
        if (hasCompleted) return
        const entranceCount = window.__entranceFramesLoadedCount ?? 0
        const scrollCount = window.__scrollFramesLoadedCount ?? 0
        const progress = Math.min(1, (entranceCount + scrollCount) / TOTAL_FRAME_COUNT)
        const percent = Math.round(progress * 100)
        loadingBarFill.style.width = `${percent}%`
        if (loadingBarText) loadingBarText.textContent = `Loading... ${percent}%`
        tryComplete()
        requestAnimationFrame(updateProgress)
      }

      const onScrollFramesLoaded = () => {
        window.removeEventListener('scroll-frames:loaded', onScrollFramesLoaded)
        tryComplete()
      }

      window.addEventListener('scroll-frames:loaded', onScrollFramesLoaded, { once: true })
      requestAnimationFrame(updateProgress)
    }

    if (lottieContainer) {
      try {
        showLoadingBarAndStartProgress()
        const lottieAnimation = this.lottie.loadAnimation({
          container: lottieContainer,
          renderer: 'svg',
          loop: false,
          autoplay: true,
          path: `/assets/lottie/intro.json?v=${lottieImgVersion}`
        })
        if (lottieAnimation) {
          lottieAnimation.addEventListener('complete', () => {
            lottieComplete = true
            tryComplete()
          })
        } else {
          lottieComplete = true
          tryComplete()
        }
      } catch (e) {
        lottieComplete = true
        tryComplete()
      }
    } else {
      lottieComplete = true
      showLoadingBarAndStartProgress()
      tryComplete()
    }

    window.addEventListener('resize', () => {
      setEntranceCanvasDimensions()
      renderEntrance()
    })
  }
}

export default EntranceAnimation